import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { IntentVerificationRow } from '@ipfabric/ui-components/IntentVerification/IntentVerification.types';
import { IntentVerificationPanel } from '@ipfabric/ui-components/IntentVerification/IntentVerificationPanel';
import type { ReportSeverityKey } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useApiTableContextSelectors } from 'components/ApiTable/model/ApiTableModel';
import { changeAdvancedFilter } from 'components/ApiTable/model/commands/changeAdvancedFilter';
import { closeAddIntentRuleForm } from 'components/ApiTable/model/commands/closeAddIntentRuleForm';
import { deleteIntentRule } from 'components/ApiTable/model/commands/deleteIntentRule';
import { openAddIntentRuleForm } from 'components/ApiTable/model/commands/openAddIntentRuleForm';
import { submitSimpleFilters } from 'components/ApiTable/model/commands/submitSimpleFilters';
import { useRuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import type { SimpleFilterField } from 'components/Table/components/SimpleFilter/types/SimpleFilterField';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useApiTableAdvancedFilterColumns } from '../useApiTableAdvancedFilterColumns';
import {
  IntentVerificationRuleForm,
  type IntentVerificationRuleFormValues,
  intentRowToFormValues,
} from './IntentVerificationRuleForm';
import type { IntentVerificationTableRow } from './intentVerificationColumns';
import { useIntentVerificationSocket } from './useIntentVerificationSocket';

const DELETE_CONFIRM_DIALOG = 'deletIntentCheckConfirmDialog';

interface Props {
  className?: string;
}

export type DeleteIntentVerificationRow = {
  id: string;
  name: string;
};

export const IntentVerification: FC<Props> = (props) => {
  const ctxProps = useApiTableContextSelectors({
    addRule: prop('intentVerifications', 'addRule'),
    advancedFilter: prop('advancedFilter'),
    dispatch: prop('dispatch'),
    reports: prop('reports'),
    tableColumns: prop('tableModel', 'columns'),
    intentFilters: prop('intentFilters'),
    onChangeIntentFilter: prop('tableModel', 'onChangeIntentFilter'),
    onChangeFilter: prop('tableModel', 'onChangeFilter'),
    simpleFilterFields: prop('simpleFilterFields'),
    onSelectFilterPreset: prop('tableModel', 'onSelectFilterPreset'),
    selectedFilterPreset: prop('tableModel', 'selectedFilterPreset'),
  });

  const ruleContext = useRuleContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger only on unmount
  useEffect(
    () => () => {
      // always reset on unmount so it reopens closed; intentionally not reset on mount
      // because we may intend to open the component in "add" state immediately
      ctxProps.dispatch(closeAddIntentRuleForm);
    },
    [],
  );

  const [deleteRow, setDeleteRow] =
    useState<DeleteIntentVerificationRow | null>(null);
  const [editRule, setEditRule] =
    useState<IntentVerificationRuleFormValues | null>(null);

  const data: IntentVerificationTableRow[] = useMemo(() => {
    const columnTitleByKey = ctxProps.tableColumns.reduce<
      Record<string, string>
    >((acc, column) => {
      acc[column.key] = column.title;
      return acc;
    }, {});

    const getColumnTitle = (column: string) => {
      if (columnTitleByKey[column]) {
        return columnTitleByKey[column]!;
      }
      stumble(`Not defined column "${column}" in intent report`);
      return column;
    };

    return Object.values(ctxProps.reports).map((report) => ({
      id: report.id,
      name: report.name,
      column: report.column,
      result: report.result,
      defaultColor: report.defaultColor,
      custom: report.custom,
      groups: report.groups,
      checks: report.checks,
      descriptions: report.descriptions,
      status: report.status,
      columnTitle: getColumnTitle(report.column),
    }));
  }, [ctxProps.reports, ctxProps.tableColumns]);

  const columns = useApiTableAdvancedFilterColumns();
  const deleteConfirm = useConfirmDialog({ name: DELETE_CONFIRM_DIALOG });

  const delteMsgs = {
    header: 'Rule will be deleted',
    subHeader: `Do you really want to delete rule "${deleteRow?.name}"?`,
    btnConfirm: 'Delete',
    btnSecondaryConfirm: 'Cancel',
  };

  const handleDeleteClick = (row: DeleteIntentVerificationRow) => {
    setDeleteRow(row);
    deleteConfirm.toggleConfirmDialog();
  };

  const handleEditClick = (row: IntentVerificationRow) => {
    const extendedRow = data.find((r) => r.id === row.id);
    if (extendedRow) {
      setEditRule(intentRowToFormValues(extendedRow, columns, ruleContext));
    }
  };

  const handleAddRuleClick = () => {
    ctxProps.dispatch(openAddIntentRuleForm);
  };

  const hasSimpleFilterValue = (
    simpleFilterField: SimpleFilterField | undefined,
  ) => {
    if (simpleFilterField === undefined) {
      return false;
    }

    const { value } = simpleFilterField;

    if (value === null) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== '';
  };

  const handleSeverityClick = (
    column: string,
    severity: ReportSeverityKey | null,
  ) => {
    if (!ctxProps.onChangeIntentFilter) {
      stumble('onChangeIntentFilter was not provided into Table from ApiTable');
      return;
    }

    // Clear selected filter preset when setting intent filter
    if (ctxProps.selectedFilterPreset) {
      ctxProps.onSelectFilterPreset?.(null);
    }

    // Clear simple filter if setting same column as intent filter
    if (severity !== null) {
      const simpleFilterField = ctxProps.simpleFilterFields?.[column];
      if (hasSimpleFilterValue(simpleFilterField)) {
        ctxProps.onChangeFilter?.({
          column,
          value: null,
          child: null,
        });
      }
    }

    ctxProps.onChangeIntentFilter({
      column,
      severity,
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteRow) {
      stumble('trying to confirm deletion without selecting row to be deleted');
      return;
    }

    setEditRule(null);
    ctxProps.dispatch(deleteIntentRule, { id: deleteRow.id });
  };

  const closeForm = async () => {
    ctxProps.dispatch(closeAddIntentRuleForm);
    setEditRule(null);
    if (ctxProps.advancedFilter.filters) {
      await ctxProps.dispatch(changeAdvancedFilter, { filters: undefined });
      ctxProps.dispatch(submitSimpleFilters);
    }
  };

  const renderContent = () => {
    if (ctxProps.addRule) {
      return (
        <IntentVerificationRuleForm
          className={props.className}
          closeForm={closeForm}
        />
      );
    }
    if (editRule !== null) {
      return (
        <IntentVerificationRuleForm
          className={props.className}
          closeForm={closeForm}
          initialValues={editRule}
          onDeleteClick={handleDeleteClick}
        />
      );
    }
    return (
      data.length > 0 && (
        <IntentVerificationPanel
          className={props.className}
          data={data}
          intentFilters={ctxProps.intentFilters}
          onAddRuleClick={handleAddRuleClick}
          onDeleteClick={handleDeleteClick}
          onEditClick={handleEditClick}
          onSeverityClick={handleSeverityClick}
        />
      )
    );
  };

  useIntentVerificationSocket();

  return (
    <>
      <ConfirmDialog
        id={DELETE_CONFIRM_DIALOG}
        msg={delteMsgs}
        onClickConfirm={handleDeleteConfirm}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      {renderContent()}
    </>
  );
};
